/* ==========================================================================
   BIG CHIEF CLOTHING — theme.js
   The stand's working parts: cart drawer with AJAX cart, quick add with size
   chips, colorway photo swaps, sticky buy bar, capture popup, scroll reveals.
   No framework, no build step. Everything degrades to plain links and forms.
   ========================================================================== */

(function () {
  'use strict';

  var BC = window.BigChief || {};
  var routes = BC.routes || {
    cart: '/cart',
    cartAdd: '/cart/add',
    cartChange: '/cart/change'
  };

  /* ------------------------------------------------------------------
     Utilities
     ------------------------------------------------------------------ */

  function $(sel, ctx) { return (ctx || document).querySelector(sel); }
  function $$(sel, ctx) { return Array.prototype.slice.call((ctx || document).querySelectorAll(sel)); }

  function money(cents) {
    var amount = (cents / 100).toFixed(2).replace(/\.00$/, '');
    return '$' + amount;
  }

  var toastEl = null;
  var toastTimer = null;
  function toast(message) {
    if (!toastEl) {
      toastEl = document.createElement('div');
      toastEl.className = 'toast';
      toastEl.setAttribute('role', 'status');
      document.body.appendChild(toastEl);
    }
    toastEl.textContent = message;
    toastEl.classList.add('is-shown');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(function () {
      toastEl.classList.remove('is-shown');
    }, 2600);
  }

  /* ------------------------------------------------------------------
     Focus trap for overlays
     ------------------------------------------------------------------ */

  function trapFocus(container) {
    function onKeydown(e) {
      if (e.key !== 'Tab') return;
      var focusables = $$('a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])', container)
        .filter(function (el) { return el.offsetParent !== null; });
      if (!focusables.length) return;
      var first = focusables[0];
      var last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
    container.addEventListener('keydown', onKeydown);
    return function () { container.removeEventListener('keydown', onKeydown); };
  }

  var scrollLockCount = 0;
  function lockScroll() {
    scrollLockCount++;
    document.documentElement.style.overflow = 'hidden';
  }
  function unlockScroll() {
    scrollLockCount = Math.max(0, scrollLockCount - 1);
    if (!scrollLockCount) document.documentElement.style.overflow = '';
  }

  /* ------------------------------------------------------------------
     AJAX cart
     ------------------------------------------------------------------ */

  function fetchCart() {
    return fetch(routes.cart + '.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); });
  }

  function addToCart(variantId, quantity) {
    return fetch(routes.cartAdd + '.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: variantId, quantity: quantity || 1 })
    }).then(function (r) {
      if (!r.ok) return r.json().then(function (err) { throw err; });
      return r.json();
    });
  }

  function changeLine(key, quantity) {
    return fetch(routes.cartChange + '.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: key, quantity: quantity })
    }).then(function (r) { return r.json(); });
  }

  /* ------------------------------------------------------------------
     Cart drawer
     ------------------------------------------------------------------ */

  var drawer = $('[data-cart-drawer]');
  var drawerReleaseTrap = null;
  var drawerOpener = null;

  function renderCart(cart) {
    if (!drawer) return;

    $$('[data-cart-count]').forEach(function (el) {
      el.textContent = cart.item_count;
      el.dataset.empty = cart.item_count === 0 ? 'true' : 'false';
      el.classList.remove('bump');
      void el.offsetWidth;
      el.classList.add('bump');
    });

    var itemsEl = $('[data-cart-items]', drawer);
    var footEl = $('[data-cart-foot]', drawer);
    var emptyTpl = $('[data-cart-empty-template]', drawer);

    if (!cart.item_count) {
      itemsEl.innerHTML = emptyTpl ? emptyTpl.innerHTML : '<p class="cart-empty">Nothing at the counter yet.</p>';
      if (footEl) footEl.hidden = true;
    } else {
      if (footEl) footEl.hidden = false;
      itemsEl.innerHTML = cart.items.map(function (item) {
        var variantLabel = item.variant_title && item.variant_title !== 'Default Title' ? item.variant_title : '';
        return (
          '<div class="cart-item" data-line-key="' + item.key + '">' +
            '<img src="' + (item.image ? item.image.replace(/(\.[a-z]+)(\?|$)/, '_200x$1$2') : '') + '" alt="" width="80" height="100" loading="lazy">' +
            '<div class="cart-item__body">' +
              '<a class="cart-item__title" href="' + item.url + '">' + item.product_title + '</a>' +
              (variantLabel ? '<span class="cart-item__variant">' + variantLabel + '</span>' : '') +
              '<div class="cart-item__qty">' +
                '<button type="button" data-qty-minus aria-label="Remove one">&minus;</button>' +
                '<span>' + item.quantity + '</span>' +
                '<button type="button" data-qty-plus aria-label="Add one">+</button>' +
              '</div>' +
            '</div>' +
            '<div class="cart-item__body" style="align-items:flex-end">' +
              '<span class="cart-item__price">' + money(item.final_line_price) + '</span>' +
              '<button type="button" class="cart-item__remove" data-line-remove>Remove</button>' +
            '</div>' +
          '</div>'
        );
      }).join('');
    }

    var subtotalEl = $('[data-cart-subtotal]', drawer);
    if (subtotalEl) subtotalEl.textContent = money(cart.total_price);

    var threshold = BC.freeShippingThreshold || 0;
    var progressEl = $('[data-ship-progress]', drawer);
    if (progressEl && threshold > 0) {
      var pct = Math.min(100, Math.round((cart.total_price / threshold) * 100));
      var bar = $('.ship-progress__fill', progressEl);
      var label = $('.ship-progress__label', progressEl);
      if (bar) bar.style.setProperty('--progress', String(pct));
      progressEl.classList.toggle('is-unlocked', cart.total_price >= threshold);
      if (label) {
        label.innerHTML = cart.total_price >= threshold
          ? 'Free shipping is locked in.'
          : 'You are <b>' + money(threshold - cart.total_price) + '</b> from free shipping.';
      }
    }

    var upsell = $('[data-cart-upsell]', drawer);
    if (upsell) {
      var inCart = cart.items.map(function (i) { return i.product_id; });
      var candidates = $$('.cart-upsell__item', upsell);
      var shown = 0;
      candidates.forEach(function (el) {
        var pid = parseInt(el.dataset.productId, 10);
        var show = inCart.indexOf(pid) === -1 && shown < 4;
        el.hidden = !show;
        if (show) shown++;
      });
      upsell.hidden = shown === 0 || cart.item_count === 0;
    }
  }

  function openDrawer() {
    if (!drawer) return;
    drawerOpener = document.activeElement;
    drawer.classList.add('is-open');
    drawer.removeAttribute('aria-hidden');
    lockScroll();
    drawerReleaseTrap = trapFocus(drawer);
    var closeBtn = $('[data-cart-close]', drawer);
    if (closeBtn) closeBtn.focus();
  }

  function closeDrawer() {
    if (!drawer) return;
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    unlockScroll();
    if (drawerReleaseTrap) { drawerReleaseTrap(); drawerReleaseTrap = null; }
    if (drawerOpener) { drawerOpener.focus(); drawerOpener = null; }
  }

  if (drawer) {
    $$('[data-cart-open]').forEach(function (el) {
      el.addEventListener('click', function (e) {
        e.preventDefault();
        fetchCart().then(renderCart);
        openDrawer();
      });
    });
    $$('[data-cart-close], .cart-drawer__scrim', drawer).forEach(function (el) {
      el.addEventListener('click', closeDrawer);
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && drawer.classList.contains('is-open')) closeDrawer();
    });

    drawer.addEventListener('click', function (e) {
      var line = e.target.closest('[data-line-key]');
      if (!line) return;
      var key = line.dataset.lineKey;
      var qtyEl = $('.cart-item__qty span', line);
      var qty = qtyEl ? parseInt(qtyEl.textContent, 10) : 1;

      if (e.target.closest('[data-qty-plus]')) {
        changeLine(key, qty + 1).then(renderCart);
      } else if (e.target.closest('[data-qty-minus]')) {
        changeLine(key, Math.max(0, qty - 1)).then(renderCart);
      } else if (e.target.closest('[data-line-remove]')) {
        changeLine(key, 0).then(renderCart);
      }
    });

    fetchCart().then(function (cart) {
      $$('[data-cart-count]').forEach(function (el) {
        el.textContent = cart.item_count;
        el.dataset.empty = cart.item_count === 0 ? 'true' : 'false';
      });
      renderCart(cart);
    }).catch(function () { /* static preview has no cart endpoints */ });
  }

  /* ------------------------------------------------------------------
     Quick add: size chips on cards, upsell buttons, fit rail
     ------------------------------------------------------------------ */

  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-quick-add]');
    if (!btn || btn.disabled) return;
    e.preventDefault();
    var variantId = parseInt(btn.dataset.quickAdd, 10);
    if (!variantId) return;
    btn.classList.add('is-busy');
    addToCart(variantId, 1)
      .then(fetchCart)
      .then(function (cart) {
        renderCart(cart);
        openDrawer();
      })
      .catch(function (err) {
        toast(err && err.description ? err.description : 'Could not add that. Try the product page.');
      })
      .then(function () { btn.classList.remove('is-busy'); });
  });

  /* ------------------------------------------------------------------
     Colorway swatches on cards swap the photo
     ------------------------------------------------------------------ */

  document.addEventListener('click', function (e) {
    var swatch = e.target.closest('.card__swatch');
    if (!swatch) return;
    e.preventDefault();
    var card = swatch.closest('.card');
    if (!card) return;
    $$('.card__swatch', card).forEach(function (s) { s.setAttribute('aria-pressed', 'false'); });
    swatch.setAttribute('aria-pressed', 'true');
    var main = $('.card__media img.card__main', card);
    if (main && swatch.dataset.image) main.src = swatch.dataset.image;
    var link = $('.card__link', card);
    if (link && swatch.dataset.url) link.href = swatch.dataset.url;
  });

  /* ------------------------------------------------------------------
     PDP: size selection, buy form, sticky bar, size guide
     ------------------------------------------------------------------ */

  var pdpForm = $('[data-product-form]');
  if (pdpForm) {
    var variantData = [];
    var dataEl = $('[data-variant-json]');
    if (dataEl) {
      try { variantData = JSON.parse(dataEl.textContent); } catch (err) { variantData = []; }
    }
    var idInput = $('input[name="id"]', pdpForm);
    var submitBtn = $('[data-atc]', pdpForm);
    var soldOutLabel = (BC.strings && BC.strings.soldOut) || 'Sold out';
    var addLabel = (BC.strings && BC.strings.addToCart) || 'Add to cart';

    $$('.size', pdpForm).forEach(function (sizeBtn) {
      sizeBtn.addEventListener('click', function () {
        if (sizeBtn.disabled) return;
        $$('.size', pdpForm).forEach(function (s) { s.setAttribute('aria-pressed', 'false'); });
        sizeBtn.setAttribute('aria-pressed', 'true');
        var vid = sizeBtn.dataset.variantId;
        if (idInput) idInput.value = vid;
        var v = variantData.filter(function (x) { return String(x.id) === String(vid); })[0];
        if (submitBtn && v) {
          submitBtn.disabled = !v.available;
          submitBtn.textContent = v.available ? addLabel : soldOutLabel;
        }
        var label = $('[data-size-label]');
        if (label) label.innerHTML = 'Size <b>' + sizeBtn.textContent.trim() + '</b>';
      });
    });

    pdpForm.addEventListener('submit', function (e) {
      if (!idInput || !idInput.value) {
        e.preventDefault();
        toast('Pick a size first.');
        var sizes = $('.pdp__sizes');
        if (sizes) sizes.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }
      if (pdpForm.dataset.ajax === 'false') return;
      e.preventDefault();
      if (submitBtn) submitBtn.classList.add('is-busy');
      addToCart(parseInt(idInput.value, 10), 1)
        .then(fetchCart)
        .then(function (cart) {
          renderCart(cart);
          openDrawer();
        })
        .catch(function (err) {
          toast(err && err.description ? err.description : 'Could not add that.');
        })
        .then(function () { if (submitBtn) submitBtn.classList.remove('is-busy'); });
    });

    /* Sticky buy bar appears once the real button scrolls away */
    var buybar = $('[data-buybar]');
    if (buybar && submitBtn && 'IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        buybar.classList.toggle('is-shown', !entries[0].isIntersecting);
      }, { rootMargin: '-60px 0px 0px 0px' });
      io.observe(submitBtn);
      var barBtn = $('[data-buybar-atc]', buybar);
      if (barBtn) {
        barBtn.addEventListener('click', function () {
          if (idInput && idInput.value) {
            pdpForm.requestSubmit ? pdpForm.requestSubmit() : pdpForm.submit();
          } else {
            var sizes = $('.pdp__sizes');
            if (sizes) sizes.scrollIntoView({ behavior: 'smooth', block: 'center' });
            toast('Pick a size first.');
          }
        });
      }
    }
  }

  /* Size guide drawer */
  var sizeGuide = $('[data-size-guide]');
  if (sizeGuide) {
    var sgRelease = null;
    var sgOpener = null;
    $$('[data-size-guide-open]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        sgOpener = document.activeElement;
        sizeGuide.classList.add('is-open');
        sizeGuide.removeAttribute('aria-hidden');
        lockScroll();
        sgRelease = trapFocus(sizeGuide);
        var close = $('[data-size-guide-close]', sizeGuide);
        if (close) close.focus();
      });
    });
    $$('[data-size-guide-close], .size-guide__scrim', sizeGuide).forEach(function (el) {
      el.addEventListener('click', function () {
        sizeGuide.classList.remove('is-open');
        sizeGuide.setAttribute('aria-hidden', 'true');
        unlockScroll();
        if (sgRelease) { sgRelease(); sgRelease = null; }
        if (sgOpener) { sgOpener.focus(); sgOpener = null; }
      });
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && sizeGuide.classList.contains('is-open')) {
        var close = $('[data-size-guide-close]', sizeGuide);
        if (close) close.click();
      }
    });
  }

  /* ------------------------------------------------------------------
     Mobile nav
     ------------------------------------------------------------------ */

  var mobileNav = $('[data-mobile-nav]');
  if (mobileNav) {
    var mnRelease = null;
    var mnOpener = null;
    $$('[data-nav-open]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        mnOpener = document.activeElement;
        mobileNav.classList.add('is-open');
        mobileNav.removeAttribute('aria-hidden');
        lockScroll();
        mnRelease = trapFocus(mobileNav);
        var close = $('[data-nav-close]', mobileNav);
        if (close) close.focus();
      });
    });
    $$('[data-nav-close]', mobileNav).forEach(function (btn) {
      btn.addEventListener('click', function () {
        mobileNav.classList.remove('is-open');
        mobileNav.setAttribute('aria-hidden', 'true');
        unlockScroll();
        if (mnRelease) { mnRelease(); mnRelease = null; }
        if (mnOpener) { mnOpener.focus(); mnOpener = null; }
      });
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && mobileNav.classList.contains('is-open')) {
        var close = $('[data-nav-close]', mobileNav);
        if (close) close.click();
      }
    });
  }

  /* ------------------------------------------------------------------
     Scroll reveals + chapter triggers
     ------------------------------------------------------------------ */

  if ('IntersectionObserver' in window) {
    var revealIO = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-in');
          revealIO.unobserve(entry.target);
        }
      });
    }, { threshold: 0.12 });

    $$('.reveal, .combos').forEach(function (el) { revealIO.observe(el); });
  } else {
    $$('.reveal, .combos').forEach(function (el) { el.classList.add('is-in'); });
  }

  /* Stagger indices for the combo stamps */
  $$('.combo').forEach(function (el, i) { el.style.setProperty('--combo-i', i); });

  /* ------------------------------------------------------------------
     Capture popup: exit intent on desktop, scroll depth on touch
     ------------------------------------------------------------------ */

  var capture = $('[data-capture]');
  if (capture && !localStorage.getItem('bc_capture_done') && !sessionStorage.getItem('bc_capture_seen')) {
    var captureShown = false;
    var capRelease = null;

    function showCapture() {
      if (captureShown) return;
      captureShown = true;
      sessionStorage.setItem('bc_capture_seen', '1');
      capture.classList.add('is-open');
      capture.removeAttribute('aria-hidden');
      lockScroll();
      capRelease = trapFocus(capture);
      var input = $('input[type="email"]', capture);
      if (input) input.focus();
    }

    function hideCapture() {
      capture.classList.remove('is-open');
      capture.setAttribute('aria-hidden', 'true');
      unlockScroll();
      if (capRelease) { capRelease(); capRelease = null; }
    }

    document.addEventListener('mouseout', function (e) {
      if (!e.relatedTarget && e.clientY <= 0) showCapture();
    });

    var depthArmed = false;
    window.addEventListener('scroll', function () {
      if (depthArmed || captureShown) return;
      var depth = (window.scrollY + window.innerHeight) / document.documentElement.scrollHeight;
      if (depth > 0.55) {
        depthArmed = true;
        if (window.matchMedia('(hover: none)').matches) {
          setTimeout(showCapture, 1600);
        }
      }
    }, { passive: true });

    $$('[data-capture-close], .capture__scrim', capture).forEach(function (el) {
      el.addEventListener('click', hideCapture);
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && capture.classList.contains('is-open')) hideCapture();
    });

    var capForm = $('form', capture);
    if (capForm) {
      capForm.addEventListener('submit', function () {
        localStorage.setItem('bc_capture_done', '1');
      });
    }
  }

  /* ------------------------------------------------------------------
     Deep links: /#board-<handle> scrolls the board and flashes the line
     ------------------------------------------------------------------ */

  if (location.hash && location.hash.indexOf('#line-') === 0) {
    var target = document.getElementById(location.hash.slice(1));
    if (target) {
      setTimeout(function () {
        target.scrollIntoView({ behavior: 'smooth', block: 'center' });
        target.style.background = 'rgba(212, 175, 91, 0.28)';
        setTimeout(function () { target.style.background = ''; }, 1600);
      }, 300);
    }
  }
})();
